import { Component, Input, OnInit, output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BreadcrumbItem,
  QaiInnerWrapComponent,
  QaiStepFlowComponent,
  QaiStepCardComponent,
  QaiQuizComponent,
  QaiLoadingComponent,
  type StepState,
  type QuizAttempt,
  type HeaderConfig,
  QaiAlertComponent,
  QaiButtonComponent,
} from '@qai/ui';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { PathwaysService } from 'src/app/services/learner/pathways.service';
import { IPathwayEnrollmentResponse, IPathwayEnrollmentStep, IPathwayNextStepResponse, IQuizAttemptResult } from 'src/app/interfaces/ipathway-enrollment';
import { PathwayEnrollmentStatus, PathwayStepState, PathwayStepType } from 'src/app/utils/enums/learner/pathway.enum';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AppConstants } from 'src/app/app-constants';
import { LocaleService } from 'src/app/services/locale.service';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';
interface PathwayStep {
  title: string;
  description: string;
  ctaLabel: string;
  ctaTooltipText: string;
  state: StepState;
  stepState: PathwayStepState;
  content: string;
  stepUuid: string;
  enrollmentStepUuid: string;
  stepType: PathwayStepType;
  needsReview: boolean;
  attemptsRemaining?: number;
  quizAttempts?: IQuizAttemptResult[];
}

@Component({
  selector: 'app-pathway-details',
  standalone: true,
  templateUrl: './pathway-details.component.html',
  styleUrl: './pathway-details.component.scss',
  imports: [
    CommonModule,
    QaiInnerWrapComponent,
    QaiStepFlowComponent,
    QaiStepCardComponent,
    QaiQuizComponent,
    QaiLoadingComponent,
    QaiAlertComponent,
    QaiButtonComponent,
    TranslocoModule,
  ],
})
export class PathwayDetailsComponent implements OnInit {
  readonly PathwayStepType = PathwayStepType;
  readonly PathwayStepState = PathwayStepState;

  onAllStepsComplete() {
    throw new Error('Method not implemented.');
  }
  isTranslationLoaded = false;
  pathwaySteps: PathwayStep[] = [];
  loading = false;
  enrollmentData: IPathwayEnrollmentResponse | null = null;
  nextStepData: IPathwayNextStepResponse | null = null;
  moduleAssignmentUuid: string | null = null;
  practiceType: string = 'PATHWAY'; // Default to PATHWAY if not provided
  hasStepOutOfAttempts = false;
  @Input() pathwayName: string = '';
  enrollmentLoaded = output<IPathwayEnrollmentResponse>();
  private reviewMaterialButtonEnabled = false;

  get isEnrollmentCompleted(): boolean {
    return this.enrollmentData?.status === PathwayEnrollmentStatus.COMPLETED;
  }

  goToLearnerDashboard(): void {
    localStorage.setItem('returnUrl', '/assignments');
    this.router.navigate(['/assignments']);
  }

  constructor(
    private translationService: TranslationService,
    private translocoService: TranslocoService,
    private localeService: LocaleService,
    private route: ActivatedRoute,
    private router: Router,
    private pathwaysService: PathwaysService,
    private featureFlagsService: FeatureFlagsService,
  ) {}

  ngOnInit(): void {
    this.featureFlagsService
      .isFlagEnabled(AppConstants.PATHWAY_REVIEW_MATERIAL)
      .then((isEnabled) => {
        this.reviewMaterialButtonEnabled = isEnabled;
      })
      .catch((error) => {
        console.error(
          'Error loading pathway review material feature flag',
          error,
        );
        this.reviewMaterialButtonEnabled = false;
      });

    this.translationService
      .loadScopedTranslation('learningPathway')
      .then(() => {
        this.isTranslationLoaded = true;
      });

    const queryParams = this.route.snapshot.queryParams;

    this.moduleAssignmentUuid = queryParams['moduleAssignmentUuid'] || null;
    this.practiceType = queryParams['practiceType'] || 'PATHWAY';

    if (this.moduleAssignmentUuid) {
      this.loadPathwayEnrollment(this.moduleAssignmentUuid);
    } else {
      console.error(
        'moduleAssignmentUuid is required to load pathway enrollment',
      );
      // No fallback data - pathwaySteps will remain empty
    }
  }

  loadPathwayEnrollment(moduleAssignmentUuid: string): void {
    this.loading = true;
    this.pathwaysService
      .getorCreatePathwayEnrollment(moduleAssignmentUuid)
      .subscribe({
        next: (enrollmentResponse: IPathwayEnrollmentResponse) => {
          this.enrollmentData = enrollmentResponse;
          this.enrollmentLoaded.emit(enrollmentResponse);

          // If enrollment doesn't exist, skip getNextStep API call
          if (!enrollmentResponse.enrollmentExists || !enrollmentResponse.enrollmentUuid) {
            this.pathwaySteps = this.extractStepsFromEnrollment(
              enrollmentResponse,
              null,
            );

            console.log(
              'extractStepsFromEnrollment - enrollment does not exist',
              this.pathwaySteps,
            );
            this.loading = false;
            return;
          }

          // If enrollment is already completed, skip getNextStep API call
          if (enrollmentResponse.status === PathwayEnrollmentStatus.COMPLETED) {
            // Extract steps directly - all will be marked as completed with no CTA buttons
            this.pathwaySteps = this.extractStepsFromEnrollment(
              enrollmentResponse,
              null,
            );

            console.log(
              'extractStepsFromEnrollment after enrollment completed',
              this.pathwaySteps,
            );
            this.loading = false;
            return;
          }
          this.pathwaysService
            .getNextStepByEnrollmentUuid(enrollmentResponse.enrollmentUuid)
            .subscribe({
              next: (nextStepResponse: IPathwayNextStepResponse) => {
                this.nextStepData = nextStepResponse;
                // Extract steps with the active step UUID from next step response
                if (this.enrollmentData) {
                  this.pathwaySteps = this.extractStepsFromEnrollment(
                    this.enrollmentData,
                    nextStepResponse.toStepUuid,
                    nextStepResponse.status,
                  );
                  console.log(
                    'extractStepsFromEnrollment after nextStepAPI',
                    this.pathwaySteps,
                  );
                }
                this.loading = false;
              },
              error: (error) => {
                console.error('Error loading next step:', error);
                this.loading = false;
                // If enrollment was loaded but next step failed, still show steps
                if (this.enrollmentData) {
                  this.pathwaySteps = this.extractStepsFromEnrollment(
                    this.enrollmentData,
                    null,
                  );
                  console.log('extractStepsFromEnrollment', this.pathwaySteps);
                }
              },
            });
        },
        error: (error) => {
          console.error('Error loading pathway enrollment:', error);
          this.loading = false;
        },
      });
  }

  extractStepsFromEnrollment(
    enrollment: IPathwayEnrollmentResponse,
    activeStepUuid: string | null = null,
    nextStepStatus: string | null = null,
  ): PathwayStep[] {
    // Reset out-of-attempts banner flag before recalculating steps
    this.hasStepOutOfAttempts = false;

    // Collect all steps from all focus area groups with their stepSequence
    const allSteps: IPathwayEnrollmentStep[] = [];
    enrollment.focusAreaGroups.forEach((group) => {
      allSteps.push(...group.steps);
    });

    // Filter out abandoned steps completely
    const filteredSteps = allSteps.filter(
      (step) => step.stepState !== PathwayStepState.ABANDONED,
    );

    // Sort by stepSequence to maintain proper order
    filteredSteps.sort((a, b) => a.stepSequence - b.stepSequence);

    // If getNextStep returned BLOCKED status with no toStepUuid,
    // find the first OUT_OF_ATTEMPTS step so we can disable all steps after it
    const outOfAttemptsIndex = (enrollment.status === PathwayEnrollmentStatus.LOCKED)
      ? filteredSteps.findIndex(s => s.stepState === PathwayStepState.OUT_OF_ATTEMPTS)
      : -1;

    // If activeStepUuid is provided from getNextStep API, mark that step as IN_PROGRESS
    // This ensures the correct step shows the "Continue" button
    if (activeStepUuid) {
      const activeStep = filteredSteps.find(
        (step) => step.stepUuid === activeStepUuid,
      );
      if (activeStep) {
        activeStep.stepState = PathwayStepState.IN_PROGRESS;
      }
    }

    // First pass: Check if there's any IN_PROGRESS step
    // This determines whether we show "Continue" or "Begin" button
    // At any moment, only one active button can appear: either "Continue" (IN_PROGRESS) or "Begin" (first NOT_STARTED)
    const hasInProgressStep = filteredSteps.some(
      (step) => step.stepState === PathwayStepState.IN_PROGRESS,
    );

    // Track state for proper mapping
    let hasFirstNotStartedStep = false;
    let hasActiveInProgressStep = false;

    // Map to PathwayStep format
    return filteredSteps.map((step: IPathwayEnrollmentStep, index: number) => {
      if (outOfAttemptsIndex !== -1 && index > outOfAttemptsIndex) {
        return {
          title: step.title,
          description: step.description,
          ctaLabel: '',
          ctaTooltipText: '',
          state: 'disabled' as StepState,
          stepState: step.stepState ?? PathwayStepState.NOT_STARTED,
          content: step.description,
          stepUuid: step.stepUuid,
          enrollmentStepUuid: step.enrollmentStepUuid ?? '',
          stepType: step.type,
          needsReview: !!step.needsReview,
        };
      }

      const stepState = step.stepState ?? PathwayStepState.NOT_STARTED;

      if (stepState === PathwayStepState.OUT_OF_ATTEMPTS) {
        this.hasStepOutOfAttempts = true;
      }

      // Check if this is the first IN_PROGRESS step (only one can be active)
      const isFirstInProgress =
        stepState === PathwayStepState.IN_PROGRESS &&
        !hasActiveInProgressStep;

      // Check if this is the first NOT_STARTED step (only if no IN_PROGRESS step exists)
      const isFirstNotStarted =
        stepState === PathwayStepState.NOT_STARTED &&
        !hasFirstNotStartedStep &&
        !hasInProgressStep;

      // Map step state to component state based on PathwayStepState enum
      const state = this.mapStepStateToComponentState(
        stepState,
        step.isFurthest,
        hasInProgressStep,
        hasActiveInProgressStep,
        hasFirstNotStartedStep,
      );

      // Update tracking flags after mapping
      if (isFirstInProgress) {
        hasActiveInProgressStep = true;
      }
      if (isFirstNotStarted) {
        hasFirstNotStartedStep = true;
      }

      // Determine CTA label based on step state
      let ctaLabel = '';
      ctaLabel = this.getCtaLabelForStep(stepState, step.type);
      const needsReview = !!step.needsReview;
      const nextQuizStep = filteredSteps
        .slice(index + 1)
        .find(s => s.type === PathwayStepType.QUIZ);

      const isNextQuizPending =
        nextQuizStep &&
        nextQuizStep.stepState !== PathwayStepState.COMPLETED;

      const ctaTooltipText =
        needsReview && isNextQuizPending
          ? this.translocoService.translate(
            'REVIEW_BEFORE_QUIZ_TOOLTIP',
            {},
            'learningPathway',
          )
        : '';

      const pathwayStep: PathwayStep = {
        title: step.title,
        description: step.description,
        ctaLabel: ctaLabel,
        ctaTooltipText: ctaTooltipText,
        state: state,
        stepState: stepState,
        content: step.description,
        stepUuid: step.stepUuid,
        enrollmentStepUuid: step.enrollmentStepUuid ?? '',
        stepType: step.type,
        needsReview: needsReview,
      };
      if (step.type === PathwayStepType.QUIZ) {
        pathwayStep.quizAttempts = step.results ?? [];
        if (step.maxAttemptsAllowed != null && step.totalAttempts != null) {
          const attemptsRemaining = this.getQuizAttemptsRemaining(
            step.maxAttemptsAllowed,
            step.totalAttempts
          );
          pathwayStep.attemptsRemaining = attemptsRemaining;
          if (
            attemptsRemaining <= 0 &&
            step.stepState !== PathwayStepState.COMPLETED
          ) {
            this.hasStepOutOfAttempts = true;
          }
        }
      }
      return pathwayStep;
    });
  }

  getQuizAttemptsRemaining(
    maxAttemptsAllowed: number,
    totalAttempts: number
  ): number {
    const used = Math.min(totalAttempts, maxAttemptsAllowed);
    return Math.max(0, maxAttemptsAllowed - used);
  }

  /**
   * Maps PathwayStepState enum values to StepState from qai/ui
   * @param stepState - The step state from the API (nullable)
   * @param isFurthest - Whether this is the furthest step reached
   * @param hasInProgressStep - Whether there's any IN_PROGRESS step in the entire list
   * @param hasActiveInProgressStep - Whether we've already activated an IN_PROGRESS step (only one can be active)
   * @param hasFirstNotStartedStep - Whether we've already seen the first NOT_STARTED step
   * @returns StepState type value from qai/ui
   */
  private mapStepStateToComponentState(
    stepState: PathwayStepState | null,
    isFurthest: boolean,
    hasInProgressStep: boolean,
    hasActiveInProgressStep: boolean,
    hasFirstNotStartedStep: boolean,
  ): StepState {
    const state = stepState ?? PathwayStepState.NOT_STARTED;

    switch (state) {
      case PathwayStepState.COMPLETED:
        // Multiple completed steps are allowed
        return 'completed' as StepState;

      case PathwayStepState.IN_PROGRESS:
        // Only one IN_PROGRESS step can be active at any moment (shows "Continue" button)
        // If we've already activated one, this one should be disabled
        // This ensures only one "Continue" button appears
        return hasActiveInProgressStep
          ? ('disabled' as StepState)
          : ('active' as StepState);

      case PathwayStepState.OUT_OF_ATTEMPTS:
        return 'active' as StepState;

      case PathwayStepState.NOT_STARTED:
        // If there's an IN_PROGRESS step (Continue button exists), all NOT_STARTED steps should be disabled
        // This ensures "Continue" and "Begin" buttons never appear at the same time
        if (hasInProgressStep) {
          return 'disabled' as StepState;
        }
        // If no IN_PROGRESS step, the very first NOT_STARTED step should be active (shows "Begin" button)
        // Subsequent NOT_STARTED steps should be disabled
        if (!hasFirstNotStartedStep) {
          return 'active' as StepState;
        }
        // Subsequent NOT_STARTED steps are disabled
        return 'disabled' as StepState;

      case PathwayStepState.STOPPED:
        // Stopped steps are treated as disabled (multiple disabled allowed)
        return 'disabled' as StepState;

      default:
        return 'disabled' as StepState;
    }
  }

  /**
   * Gets the appropriate CTA label based on step state
   * @param stepState - The step state from the API (nullable)
   * @returns CTA label string
   */
  private getCtaLabelForStep(
    stepState: PathwayStepState | null,
    stepType: PathwayStepType,
  ): string {
    const state = stepState ?? PathwayStepState.NOT_STARTED;

    if (stepType === PathwayStepType.QUIZ) {
      return '';
    }

    switch (state) {
      case PathwayStepState.IN_PROGRESS:
        return this.translocoService.translate(
          'CONTINUE',
          {},
          'learningPathway',
        );

      case PathwayStepState.OUT_OF_ATTEMPTS:
        return '';

      case PathwayStepState.COMPLETED:
        if (stepType === PathwayStepType.INTRO) {
          return this.translocoService.translate(
            'ASK_QUESTIONS_ABOUT_PATHWAY',
            {},
            'learningPathway',
          );
        }
        if (!this.reviewMaterialButtonEnabled) {
          return '';
        }
        return this.translocoService.translate(
          'REVIEW_MATERIAL',
          {},
          'learningPathway',
        );

      case PathwayStepState.NOT_STARTED:
        return this.translocoService.translate('BEGIN', {}, 'learningPathway');

      case PathwayStepState.STOPPED:
        // Stopped steps are treated as disabled, but if shown, use Continue
        return this.translocoService.translate(
          'CONTINUE',
          {},
          'learningPathway',
        );

      case PathwayStepState.ABANDONED:
        // This case should not be reached as abandoned steps are filtered out
        return this.translocoService.translate('BEGIN', {}, 'learningPathway');

      default:
        return this.translocoService.translate('BEGIN', {}, 'learningPathway');
    }
  }

  /**
   * Get the current enrollment status
   * @returns The enrollment status enum value, or null if no enrollment data
   */
  getEnrollmentStatus(): PathwayEnrollmentStatus | null {
    return this.enrollmentData?.status || null;
  }

  /**
   * Check if enrollment is in a specific status
   * @param status - The status to check
   * @returns True if enrollment is in the specified status
   */
  isEnrollmentStatus(status: PathwayEnrollmentStatus): boolean {
    return this.enrollmentData?.status === status;
  }

  /**
   * Handles step button click (Begin/Continue/Review material)
   * Navigates to the call route with required parameters
   * @param step - The pathway step that was clicked
   */
  onStepClick(step: PathwayStep): void {
    if (!this.enrollmentData || !this.moduleAssignmentUuid) {
      console.error('Enrollment data or moduleAssignmentUuid is missing');
      return;
    }

    // Check if enrollment doesn't exist - need to create it first
    if (!this.enrollmentData.enrollmentExists && !this.enrollmentData.enrollmentUuid) {
      console.log('Enrollment does not exist, creating enrollment before proceeding');
      this.loading = true;

      this.pathwaysService.createEnrollment(this.moduleAssignmentUuid).subscribe({
        next: (enrollmentResponse: IPathwayEnrollmentResponse) => {
          console.log('Enrollment created successfully:', enrollmentResponse);
          this.enrollmentData = enrollmentResponse;
          
          // Now navigate to call route with the new enrollmentUuid
          const enrollmentId = enrollmentResponse.enrollmentUuid;
          const stepUuid = step.enrollmentStepUuid || enrollmentResponse.activeEnrollmentStepUuid;
          const stepType = step.stepType;

          this.loading = false;

          this.router.navigate(['/call'], {
            queryParams: {
              enrollmentUuid: enrollmentId,
              moduleAssignmentUuid: this.moduleAssignmentUuid,
              enrollmentStepUuid: stepUuid,
              stepType: stepType,
              practiceType: this.practiceType,
            },
          });
        },
        error: (error) => {
          console.error('Error creating enrollment:', error);
          this.loading = false;
        },
      });

      return;
    }

    const enrollmentId = this.enrollmentData.enrollmentUuid;
    const stepUuid = step.enrollmentStepUuid;
    const stepType = step.stepType;

    // Navigate to call route with query parameters
    // Note: For PATHWAY type, stepUuid is used instead of callUuid
    this.router.navigate(['/call'], {
      queryParams: {
        enrollmentUuid: enrollmentId,
        moduleAssignmentUuid: this.moduleAssignmentUuid,
        enrollmentStepUuid: stepUuid,
        stepType: stepType,
        practiceType: this.practiceType,
      },
    });
  }

  getBreadcrumbItems(): BreadcrumbItem[] {
    return [
      {
        text: this.translocoService.translate(
          'ASSIGNMENTS',
          {},
          'learningPathway',
        ),
        href: '/assignments',
      },
      { text: '', href: '', active: true },
    ];
  }

  getOverallScoreTooltip(): string {
    return this.translocoService.translate('QUIZ_OVERALL_SCORE_TOOLTIP', {}, 'learningPathway');
  }

  getQuizColumnHeaders(): HeaderConfig[] {
    const scope = 'learningPathway';
    return [
      { name: this.translocoService.translate('QUIZ_COLUMN_DATE', {}, scope), sortable: false },
      { name: this.translocoService.translate('QUIZ_COLUMN_OVERALL_SCORE', {}, scope), sortable: false },
      { name: this.translocoService.translate('QUIZ_COLUMN_ANSWER_POINTS', {}, scope), sortable: false },
      { name: this.translocoService.translate('QUIZ_COLUMN_RESULTS', {}, scope), sortable: false, clickable: true },
    ];
  }

  getSortedQuizAttempts(step: PathwayStep): QuizAttempt[] {
    if (!step.quizAttempts?.length) return [];

    const isStepCompleted = step.stepState === PathwayStepState.COMPLETED;
    const sorted = step.quizAttempts.slice().sort((a, b) => {
      if (isStepCompleted) {
        if (b.overAllScore !== a.overAllScore) {
          return b.overAllScore - a.overAllScore;
        }
      }
      return new Date(b.scoredAt).getTime() - new Date(a.scoredAt).getTime();
    });

    return this.mapQuizAttempts(sorted);
  }

  mapQuizAttempts(results: IQuizAttemptResult[] | undefined): QuizAttempt[] {
    if (!results?.length) return [];
    return results.map((r) => ({
      id: r.practiceCallUuid,
      date: this.localeService.formatShortDateTimeWithTimezone(r.scoredAt),
      overallScore: `${r.overAllScore}%`,
      answerPoints: `${r.correctAnswerPoints}/${r.totalAnswerPoints}`,
    }));
  }

  onViewFeedbackClick(payload: { attempt: QuizAttempt }): void {
    const practiceCallUuid = payload?.attempt?.id;
    if (!practiceCallUuid) return;

    this.router.navigate(['/results_guided'], {
      queryParams: {
        practiceCallUuid,
        practiceType: this.practiceType,
        pathwayName: this.pathwayName,
      },
    });
  }

  onViewAllAttemptsClick(step: PathwayStep): void {
    console.log("viewallattempts button click")
  }
}
